/**
 * Platform detection and native capability access.
 *
 * Abstracts Tauri-specific APIs so features can use native capabilities
 * without caring whether they're running in WKWebView or a browser.
 * The haptics plugin is dynamically imported so it never gets bundled
 * into web builds. Silent catch handles simulators and unsupported hardware.
 *
 * Used by any component that needs to know if it's running natively
 * or wants to trigger haptic feedback on user interactions.
 */
import { useCallback } from "react";

export function usePlatform() {
  // __TAURI_INTERNALS__ is injected by the Tauri runtime into the WKWebView
  const isNative = typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

  const haptic = useCallback(async (style: "light" | "medium" | "heavy") => {
    if (!isNative) return;
    try {
      const { impactFeedback } = await import("@tauri-apps/plugin-haptics");
      await impactFeedback(style);
    } catch {}
  }, [isNative]);

  return { isNative, haptic };
}
